import { Container } from '@acies/core';
import { CloseButton, Dialog, Divider, Spinner } from '@acies/ui';
import { ConfiguratorModel } from '../configurator.model';
import DrawingViewer from '../drawing-section/drawing-viewer';
import PositionErrorMessage from '../shared/position-error-message/position-error-message';
import GeometryMeasurements from './geometry-measurements';
import GeometrySelectList from './geometry-select-list';
import GeometrySelectListEvent from './geometry-select-list-event';

/** A Divider stood on its end: Divider is a horizontal rule by default, and these separate columns. */
function columnDivider(): Divider {
	const divider = new Divider();
	divider.width = 1;
	divider.height = 'fill';
	return divider;
}

/** The dialog the shape of a position is picked in, which `GeometryOverlay` centres on the screen. */
export default class GeometryDialog extends Dialog {
	#closeButton!: CloseButton;
	#drawing!: Container;
	#drawingViewer!: DrawingViewer;
	#positionErrorMessage!: PositionErrorMessage;
	#spinner!: Spinner;
	#leftDivider!: Divider;
	#shapes!: GeometrySelectList;
	#rightDivider!: Divider;
	#measurements!: GeometryMeasurements;
	public constructor() {
		super();
		this.width = 1280;
		this.icon = 'transform';
		this.text = 'Geometri';
		// the dialog is as tall as its tallest column, so this is that height; it would take whatever the
		// header and footer leave, were the dialog ever given a height of its own
		this.content.height = 'fill';
		// three columns side by side, rather than the rows Dialog stacks its content in by default
		this.content.autoLayout = 'horizontal';
		this.content.gapHorizontal = 24;
		// the listener is registered on the model, so it would otherwise run in its scope rather
		// than in this one; subscribed in initialize, for the reason given there
		this.columnChanged = this.columnChanged.bind(this);
	}

	/**
	 * The column the drawing is shown in, and the refusal in its place.
	 *
	 * Three children and one on show: the spinner while a drawing is on its way, the error card while something is wrong, and the viewer otherwise — see {@link GeometryDialog.columnChanged}. Centred, so the spinner and the card sit in the middle of the room the drawing had; the viewer fills it either way.
	 */
	public get drawing(): Container {
		if (!this.#drawing) {
			this.#drawing = new Container();
			this.#drawing.width = 'fill';
			this.#drawing.height = 'fill';
			this.#drawing.alignment = 'center';
		}
		return this.#drawing;
	}

	/** The drawing of the position, in the first column. It follows the model itself, and fills its own size by default, so it takes the width the shapes and the measurements leave. */
	public get drawingViewer(): DrawingViewer {
		if (!this.#drawingViewer) {
			this.#drawingViewer = new DrawingViewer();
		}
		return this.#drawingViewer;
	}

	/** What went wrong the last time the position was sent to the engine, shown where the drawing was. It reads the model and shows itself; the dialog only makes room for it. */
	public get positionErrorMessage(): PositionErrorMessage {
		if (!this.#positionErrorMessage) {
			this.#positionErrorMessage = new PositionErrorMessage();
			// as tall as its message and no taller, unlike under the properties, where the card fills
			// what the rows leave: a filling height stretches to the top of the column, and the point
			// of the column being centred is that the card sits in the middle of it
			this.#positionErrorMessage.height = 'hug';
		}
		return this.#positionErrorMessage;
	}

	/** Says that a drawing is on its way, in the middle of the room it will take. Shown only while a call is out and there is no drawing — see {@link GeometryDialog.columnChanged}, and `DrawingSection`, which waits the same way. */
	public get spinner(): Spinner {
		if (!this.#spinner) {
			this.#spinner = new Spinner();
			this.#spinner.visible = false;
		}
		return this.#spinner;
	}

	/** The line between the drawing and the shapes. */
	public get leftDivider(): Divider {
		if (!this.#leftDivider) {
			this.#leftDivider = columnDivider();
		}
		return this.#leftDivider;
	}

	/** The column the shapes are picked in. As wide as four shapes by default, so the drawing has what is left once the measurements have theirs. */
	public get shapes(): GeometrySelectList {
		if (!this.#shapes) {
			this.#shapes = new GeometrySelectList();
		}
		return this.#shapes;
	}

	/** The line between the shapes and the measurements. */
	public get rightDivider(): Divider {
		if (!this.#rightDivider) {
			this.#rightDivider = columnDivider();
		}
		return this.#rightDivider;
	}

	/** The column the measurements of the shape are given in. It follows the model itself, and is told by the dialog which shape the shapes show — see #shapeSelected. */
	public get measurements(): GeometryMeasurements {
		if (!this.#measurements) {
			this.#measurements = new GeometryMeasurements();
		}
		return this.#measurements;
	}

	/**
	 * The button that closes the dialog, in the footer where the actions that end a dialog are looked for.
	 *
	 * Its dispatcher names this dialog, so whoever listens for a CloseButtonEvent above it can tell this one from the close button of any other dialog.
	 */
	public get closeButton(): CloseButton {
		if (!this.#closeButton) {
			this.#closeButton = new CloseButton();
			this.#closeButton.dispatcher = 'geometryDialog';
		}
		return this.#closeButton;
	}

	/** Listened for before the columns are added, since the shapes say which shape they show as soon as they are in the document. */
	protected override initialize(): void {
		super.initialize();
		this.addEventListener(GeometrySelectListEvent.SHAPE_SELECTED, this.#shapeSelected as EventListener);
		this.drawing.addComponents([this.drawingViewer, this.positionErrorMessage, this.spinner]);
		this.content.addComponents([this.drawing, this.leftDivider, this.shapes, this.rightDivider, this.measurements]);
		this.footer.addComponent(this.closeButton);
		// Subscribed here rather than in the constructor, and only once the card has been made: the
		// card sets its own visibility from the error, and listeners are told in the order they
		// subscribed, so the one that subscribes last has the last word. The dialog has to have it,
		// to keep the card down while the spinner is up — see columnChanged. What the model already
		// holds is read straight after, since a subscription only hears what changes from here on.
		ConfiguratorModel.currentDrawing.subscribe(this.columnChanged);
		ConfiguratorModel.currentError.subscribe(this.columnChanged);
		ConfiguratorModel.configurationInProgress.subscribe(this.columnChanged);
		ConfiguratorModel.calculationInProgress.subscribe(this.columnChanged);
		this.columnChanged();
	}

	/**
	 * Unsubscribes, because the model outlives the dialog.
	 *
	 * `ConfiguratorModel` is a module constant, so a listener it still holds is a dialog that has been removed and cannot be collected.
	 */
	protected override disconnected(): void {
		super.disconnected();
		ConfiguratorModel.currentDrawing.unsubscribe(this.columnChanged);
		ConfiguratorModel.currentError.unsubscribe(this.columnChanged);
		ConfiguratorModel.configurationInProgress.unsubscribe(this.columnChanged);
		ConfiguratorModel.calculationInProgress.unsubscribe(this.columnChanged);
	}

	/**
	 * Gives the drawing's room to one of three things: the spinner while a drawing is on its way, the error card while something is wrong, and the viewer otherwise.
	 *
	 * One rule for the three rather than one each, because the same values tell them apart. A call out for the selected position and no drawing yet is waiting — see `DrawingSection`, which waits the same way — and a refusal with no call out is an error to read. Waiting comes first: an error still on the position while
	 * the next answer is on its way is about the answer before, so the card is kept down until the new one has landed, refused or not. The card sets its own visibility from the error, which is why the dialog is told after it — see the subscriptions in initialize.
	 *
	 * Whichever two are not on show are taken out of the layout rather than left empty, because the viewer fills whatever it is given and would push the others aside. The drawing itself is untouched — it is the model's, and the drawing section goes on showing it — so it is back the moment the error clears. For a
	 * refused shape the drawing is already down by then; for a refused length the message takes its place until an answer goes through.
	 */
	private columnChanged(): void {
		const error = ConfiguratorModel.currentError.value;
		const waiting = (ConfiguratorModel.configurationInProgress.value || ConfiguratorModel.calculationInProgress.value) && ConfiguratorModel.currentDrawing.value === null;
		this.spinner.visible = waiting;
		this.spinner.spin = waiting;
		this.positionErrorMessage.visible = !waiting && error !== null;
		this.drawingViewer.visible = !waiting && error === null;
	}

	/**
	 * Hands the shape the shapes show on to the measurements, which are only shown beside the shape the position is cut to.
	 *
	 * Through the dialog because the two are columns side by side, and an event from one does not reach the other. Stopped here, because this is where it is answered: nothing above the dialog has a use for it.
	 */
	#shapeSelected(event: GeometrySelectListEvent): void {
		event.stopPropagation();
		this.measurements.selectedShape = event.shapeType;
	}
}
customElements.define('cf-geometry-dialog', GeometryDialog);
